import React from 'react';
import * as Babel from '@babel/standalone';

declare global {
  interface Window {
    FORM_CONFIG?: unknown;
    formConfig?: unknown;
  }
}

// ---------------------------------------------------------------------------
// Strip module-level export syntax that is invalid inside new Function.
// This is the ONLY source transformation the engine applies.
// All other correctness is the server's responsibility.
// ---------------------------------------------------------------------------
export const sanitizeComponentSource = (source: string): string =>
  source
    .replace(/export\s+default\s+FormComponent;?/g, '')
    .replace(/export\s+const\s+FormComponent/g, 'const FormComponent')
    .replace(/export\s+function\s+FormComponent/g, 'function FormComponent');

export interface CompileOptions {
  hostFunctions?: Record<string, unknown>;
  data?: Record<string, any> | null;
  formConfig?: Record<string, any> | null;
}

// ---------------------------------------------------------------------------
// Compile and execute a server-generated FormComponent string.
//
// Pipeline:
//   1. Strip export keywords   — module syntax is invalid inside new Function
//   2. Babel JSX transpile     — throws with Babel's original error if invalid
//   3. Execute via new Function — throws if FormComponent is not defined
//
// No heuristic source transforms. When the server generates invalid code the
// original error message is surfaced so it can be fixed at the source.
// ---------------------------------------------------------------------------
export const compileComponent = (
  componentSource: string,
  { hostFunctions = {}, data = null, formConfig = null }: CompileOptions = {}
): React.ComponentType => {
  const source = sanitizeComponentSource(componentSource);

  const runtimeFormConfig =
    formConfig ?? window.FORM_CONFIG ?? window.formConfig ?? null;

  const argNames = [
    ...Object.keys(hostFunctions),
    'React',
    'data',
    'FORM_CONFIG',
    'formConfig',
  ];
  const argValues = [
    ...Object.values(hostFunctions),
    React,
    data ?? {},
    runtimeFormConfig,
    runtimeFormConfig,
  ];

  // Transpile JSX → plain JS. Throws with Babel's original message (including
  // line/column) if the source has a syntax error — surfaces directly to the
  // error UI so the server team sees the exact problem.
  let transformed: string;
  try {
    transformed = Babel.transform(source, { presets: ['react'] }).code!;
  } catch (err) {
    throw new Error(
      `Babel compilation failed: ${err instanceof Error ? err.message : String(err)}`
    );
  }

  // Execute. Throws if FormComponent is not defined or if there is a JS
  // runtime error at module evaluation time.
  const returnStatement =
    "\nif (typeof FormComponent === 'undefined') " +
    "throw new Error('FormComponent was not defined by the generated source.');" +
    "\nreturn FormComponent;";

  const fn = new Function(...argNames, transformed + returnStatement);
  return fn(...argValues) as React.ComponentType;
};
